import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';
import { getConfig } from '../config/config';

interface KeycloakJwtPayload extends JwtPayload {
  realm: string;
  name: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  email: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: Record<
    string,
    {
      roles: string[];
    }
  >;
  groups?: string[];
  scope?: string;
  sid?: string;
  nonce?: string;
  auth_time?: number;
  session_state?: string;
  azp?: string;
  typ?: string;
  user_created_timestamp?: number;
  relation?: string;
  'allowed-origins'?: string[];
}

const getTenant = () => {
  const params = new URLSearchParams(location.search);
  const tenant = params.get('tenant');
  return tenant || localStorage.getItem('tenant') || getConfig().defaultTenant;
};

const getLanguage = () => {
  const params = new URLSearchParams(location.search);
  const language = params.get('currentLang');
  return language || 'en';
};

const getkeycloakClientName = () => {
  const params = new URLSearchParams(location.search);
  const clientId = params.get('clientId');
  return clientId || getConfig().keycloakClientName;
};

const getOnBoardingRedirectUrl = () => {
  const params = new URLSearchParams(location.search);
  const url = params.get('onBoardingRedirectUrl');
  return url || getConfig().userManagementEndpoint;
};

const getInvitationEmailLifeSpan = () => {
  const params = new URLSearchParams(location.search);
  const lifespan = params.get('invitationEmailLifeSpan');
  if (lifespan) {
    return lifespan;
  }
  return undefined;
};

const getParentLoginInfo = () => {
  const params = new URLSearchParams(location.search);
  const currentUserId = params.get('currentUserId');
  const tokenUserIdKey = params.get('tokenUserIdKey');
  return {
    currentUserId,
    tokenUserIdKey,
  };
};

const getParentOrigin = () => {
  const params = new URLSearchParams(location.search);
  const parentOrigin = params.get('parentOrigin');
  return parentOrigin || undefined;
};

const getKeycloakInitConfig = () => {
  const tokens = JSON.parse(localStorage.getItem('kc') ?? '{}');
  const decodedToken = tokens?.token
    ? jwtDecode<KeycloakJwtPayload>(tokens?.token)
    : undefined;
  const parentLoginInfo = getParentLoginInfo();
  if (
    parentLoginInfo?.currentUserId &&
    parentLoginInfo?.tokenUserIdKey &&
    decodedToken &&
    parentLoginInfo.currentUserId !==
      decodedToken[parentLoginInfo?.tokenUserIdKey as keyof KeycloakJwtPayload]
  ) {
    localStorage.removeItem('kc');
    return {
      onLoad: 'login-required',
      checkLoginIframe: false,
      pkceMethod: 'S256',
    };
  }

  return {
    onLoad: 'login-required',
    checkLoginIframe: false,
    token: tokens?.token,
    refreshToken: tokens?.refreshToken,
    pkceMethod: 'S256',
  };
};

const setTokenToLocalStorage = (tokens: {
  token?: string;
  refreshToken?: string;
}) => {
  const { token, refreshToken } = tokens;
  localStorage.setItem('kc', JSON.stringify({ token, refreshToken }));
  if (token) {
    const decodedToken = jwtDecode<KeycloakJwtPayload>(token);
    localStorage.setItem('tenant', decodedToken.realm);
  }
};

export {
  getTenant,
  getLanguage,
  getkeycloakClientName,
  getOnBoardingRedirectUrl,
  getKeycloakInitConfig,
  setTokenToLocalStorage,
  getInvitationEmailLifeSpan,
  getParentOrigin,
};
